import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentStateService } from '../content-state.service';
import { ImageBuilderDraft, ImageGeneratorService } from '../image-generator.service';

type ViewMode = 'LIST' | 'SETUP' | 'LOADING';

@Component({
  selector: 'app-image-generator',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatIconModule, MatInputModule, MatSelectModule, MatTooltipModule, MatProgressSpinnerModule, TranslateModule, MatDividerModule],
  templateUrl: './image-generator.component.html',
  styleUrls: ['./image-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageGeneratorComponent {
  @Output() close = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private imageService = inject(ImageGeneratorService);
  private confirmMenuService = inject(ConfirmMenuService);
  private contentStateService = inject(ContentStateService);
  private authService = inject(AuthService);
  private errorHandler = inject(ErrorHandlerService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  courseId = input.required<string>();

  setupForm: FormGroup;

  // State
  drafts = signal<ImageBuilderDraft[]>([]);
  visibleDrafts = computed(() => this.drafts().filter((d) => d.status !== 'draft'));
  activeDraftId = signal<string | null>(null);
  viewMode = signal<ViewMode>('LIST');
  attachedFiles = signal<{ id: string; file?: File; name: string; type: string; url?: string; uploading: boolean; filename?: string }[]>([]);

  cannotUseAI = computed(() => !this.authService.canUseAI());

  aspectRatios = [
    { value: '1:1', label: 'ImageGenerator.AspectRatio.Square' },
    { value: '16:9', label: 'ImageGenerator.AspectRatio.Landscape' },
    { value: '9:16', label: 'ImageGenerator.AspectRatio.Portrait' },
    { value: '4:3', label: 'ImageGenerator.AspectRatio.Standard' },
    { value: '3:4', label: 'ImageGenerator.AspectRatio.Portrait34' },
  ];

  models = [
    { value: 'fast', label: 'ImageGenerator.Model.Fast' },
    { value: 'pro', label: 'ImageGenerator.Model.Pro' },
  ];

  constructor() {
    this.setupForm = this.fb.group({
      prompt: ['', [Validators.required, Validators.minLength(3)]],
      aspectRatio: ['16:9', Validators.required],
      model: ['pro'],
    });

    effect(() => {
      if (this.courseId()) {
        this.loadDrafts();
      }
    });
  }

  loadDrafts() {
    this.imageService.getDrafts(this.courseId()).subscribe((drafts) => {
      this.drafts.set(drafts);
    });
  }

  getDraftTitle(draft: ImageBuilderDraft): string {
    if (!draft.versions.length) return 'Untitled';
    return draft.versions[draft.versions.length - 1].prompt;
  }

  createNew() {
    if (!this.authService.canUseAI()) return;
    const existingSetupDraft = this.drafts().find((d) => d.status === 'draft');
    if (existingSetupDraft) {
      this.activeDraftId.set(existingSetupDraft._id);
      this.attachedFiles.set(
        existingSetupDraft.mediaContexts?.map((m: any) => ({ id: 'initial_' + Date.now(), name: m.filename || 'File', type: m.type || 'image', url: m.url, uploading: false })) ||
          []
      );
      this.setupForm.reset({ aspectRatio: '16:9', model: 'pro' });
      this.viewMode.set('SETUP');
      return;
    }

    this.setupForm.reset({ aspectRatio: '16:9', model: 'pro' });
    this.viewMode.set('SETUP');
    this.imageService.createDraft({ contentId: this.courseId() }).subscribe((draft) => {
      this.activeDraftId.set(draft._id);
      this.drafts.update((drafts) => [draft, ...drafts]);
    });
  }

  cancelSetup() {
    this.attachedFiles.set([]);
    this.activeDraftId.set(null);
    this.viewMode.set('LIST');
  }

  openDraft(draft: ImageBuilderDraft) {
    if (draft.status === 'generating') {
      this.viewMode.set('LOADING');
    } else {
      this.contentStateService.openImageRefiner(draft);
    }
  }

  generate() {
    if (this.setupForm.invalid) return;
    const { prompt, aspectRatio, model } = this.setupForm.value;

    this.viewMode.set('LOADING');

    const readyAttachments = this.attachedFiles().filter((f) => !f.uploading && f.url);
    const mediaContexts = readyAttachments.map((f) => ({ url: f.url!, type: f.type, mimeType: 'image/jpeg', filename: f.name }));

    this.imageService
      .generateImage({
        contentId: this.courseId(),
        prompt: prompt!,
        aspectRatio: aspectRatio!,
        model: model!,
        mediaContexts: mediaContexts.length > 0 ? mediaContexts : undefined,
        draftId: this.activeDraftId() || undefined,
      })
      .subscribe({
        next: (draft) => {
          this.loadDrafts();
          this.openDraft(draft);
          this.viewMode.set('LIST');
          this.attachedFiles.set([]);
          this.activeDraftId.set(null);
        },
        error: (err) => {
          console.error(err);
          this.errorHandler.handleAiRestrictedError(err);
          this.viewMode.set('SETUP');
        },
      });
  }

  deleteDraft(draftId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
      this.confirmMenuService.confirm(event.target as HTMLElement, 'Global.AreYouSure', 'delete_outline').subscribe(() => {
        this.doDelete(draftId);
      });
    } else {
      this.doDelete(draftId);
    }
  }

  private doDelete(draftId: string) {
    this.imageService.deleteDraft(draftId).subscribe(() => {
      this.loadDrafts();
    });
  }

  // --- Attachments & Voice ---

  allFilesDoneUploading() {
    return this.attachedFiles().every((f) => !f.uploading);
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0]; // Only allow 1 image for now
    if (!file.type.startsWith('image/')) {
      this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
      return;
    }

    // Remove any existing image before adding new one
    const existingImage = this.attachedFiles().find((f) => f.type === 'image');
    if (existingImage) {
      this.removeAttachment(existingImage.id);
    }

    const fileId = Date.now().toString();
    this.attachedFiles.update((filesArray) => [...filesArray, { id: fileId, file, name: file.name, type: 'image', uploading: true }]);
    this.uploadFileInternal(file, fileId);
    event.target.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string) {
    try {
      const draftId = this.activeDraftId() || this.courseId();
      const filename = `image_generator_attachments/${draftId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

      this.attachedFiles.update((filesArray) => filesArray.map((f) => (f.id === fileId ? { ...f, url: gsUrl, filename, uploading: false } : f)));
      this.saveMediaContexts();
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string) {
    const fileToRemove = this.attachedFiles().find((f) => f.id === fileId);
    if (fileToRemove && fileToRemove.filename) {
      this.authService.deleteFile(fileToRemove.filename).catch((err) => console.error('Failed to delete file from storage', err));
    }
    this.attachedFiles.update((filesArray) => filesArray.filter((f) => f.id !== fileId));
    this.saveMediaContexts();
  }

  saveMediaContexts() {
    if (!this.activeDraftId()) return;
    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    const mediaContexts = readyAttachments.map((a) => ({ url: a.url!, type: a.type, filename: a.name }));
    this.imageService.updateDraft(this.activeDraftId()!, { mediaContexts }).subscribe((updatedDraft) => {
      this.drafts.update((drafts) => drafts.map((d) => (d._id === updatedDraft._id ? updatedDraft : d)));
    });
  }
}
